/**
 * Page des actions spontanées d'un contact : affiche les actions de tous les utilisateurs.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getContact, getContactActions } from "../api/contactApi";
import { deleteAction } from "../api/spontaneousActionApi";
import { useAuth } from "../contexts/AuthContext";
import SortableTable from "../components/SortableTable";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatDate } from "../utils/dateUtils";
import "./PageStyles.css";

export default function ContactActionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contact, setContact] = useState(null);
  const [actions, setActions] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    const [contactData, actionsData] = await Promise.all([
      getContact(id),
      getContactActions(id),
    ]);
    setContact(contactData);
    setActions(actionsData);
  }

  async function handleDelete() {
    await deleteAction(deleteId);
    setDeleteId(null);
    loadData();
  }

  const isOwner = contact && contact.owner_id === user?.id;
  const fullName = contact
    ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
    : "";

  const columns = [
    { key: "owner_name", label: "Créateur" },
    { key: "action_date", label: "Date" },
    { key: "description", label: "Description" },
    { key: "responsable_name", label: "Responsable" },
    { key: "tags", label: "Tags" },
    { key: "operations", label: "" },
  ];

  function renderRow(a) {
    const isMine = a.owner_id === user?.id;
    return (
      <tr key={a.id}>
        <td>{a.owner_name || "—"}</td>
        <td>{formatDate(a.action_date)}</td>
        <td title={a.description}>
          {(a.description || "").substring(0, 80)}
          {(a.description || "").length > 80 ? "..." : ""}
        </td>
        <td>{a.responsable_name || "—"}</td>
        <td>
          <div className="tag-list">
            {a.tags?.map((tag) => (
              <span key={tag.id} className="tag-inline">{tag.label}</span>
            ))}
          </div>
        </td>
        <td className="row-actions">
          {isMine && (
            <>
              <button className="btn btn-small" onClick={() => navigate(`/actions/${a.id}/edit`)}>
                Modifier
              </button>
              <button className="btn btn-small btn-danger" onClick={() => setDeleteId(a.id)}>
                Supprimer
              </button>
            </>
          )}
        </td>
      </tr>
    );
  }

  if (!contact) return <div className="page"><p>Chargement...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Actions Réalisées — {fullName || "Contact"}</h2>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {isOwner && (
            <Link to={`/actions/new?contact_id=${id}`} className="btn btn-primary">
              Nouvelle action
            </Link>
          )}
          <Link to={`/contacts/${id}`} className="btn btn-secondary">
            Retour au contact
          </Link>
        </div>
      </div>
      <SortableTable
        columns={columns}
        rows={actions}
        renderRow={renderRow}
        emptyMessage="Aucune action réalisée pour ce contact."
      />
      {deleteId && (
        <ConfirmDialog
          message="Supprimer cette action réalisée ?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}
